/**
 * @file BlackBox.js
 * @project Web-panel
 * @author Pavel Shabardin (<bigbn@mail.ru>) Thursday, 28th November 2019 3:59:59 pm
 * @copyright 2015 - 2019 SKAT LLC, Delive LLC
 * @flow
 */
// $FlowFixMe[untyped-type-import]
import type { Observable, Subscription } from 'rxjs'
import type { PartialState, CommitPayload, Resolver, iEventEmitter, iLogger, EmitterEvents, CombinedValue } from '../types'

import { Injectable, Inject } from '../serviceLocator'
import EventEmitter from 'events'
import { from, merge, fromEvent } from 'rxjs'
import { groupBy, tap, filter, map, flatMap, distinctUntilChanged, switchMap, skipWhile } from 'rxjs/operators'
import { getUpdates } from './mList'
import omit from 'lodash/omit'
import isNil from 'lodash/isNil'

@Injectable('stateStreamsManager')
class StateStreamsManager<S> extends EventEmitter implements iEventEmitter {
  @Inject logger: iLogger

  // $FlowFixMe[value-as-type]
  dataChanges$ : Observable<CommitPayload<S>>
  // $FlowFixMe[value-as-type]
  dataCommits$ : Observable<CommitPayload<S>>

  dataApplies$ : Observable<CommitPayload<S>>
  // $FlowFixMe[value-as-type]
  subscribtion: Subscription
  resolvers: Array<Resolver<S>>
  keys: Array<any>
  _destructors: {[string]:CombinedValue}

  static get EVENT () : EmitterEvents {
    return {
      LOCK: 'lock',
      UNLOCK: 'unlock',
      DATA_CHANGED: 'dataChanged',
      DATA_COMMITTED: 'dataCommitted',
      DATA_APPLIED: 'dataApplied'
    }
  }

  get EVENT () : EmitterEvents {
    return StateStreamsManager.EVENT
  }

  set destructors (value: {[string]:CombinedValue}) {
    this._destructors = value
  }

  constructor () {
    super()
    this.resolvers = []
    this.keys = []
    this._destructors = {}
    this.logger.namespace = 'state-stream-manager'
    this.dataChanges$ = fromEvent(this, this.EVENT.DATA_CHANGED)
    this.dataCommits$ = fromEvent(this, this.EVENT.DATA_COMMITTED)
    this.dataApplies$ = fromEvent(this, this.EVENT.DATA_APPLIED)
  }

  getCompoundKey (a: any, b: any, c: any, d: any) : number {
    let returnValue = null
    const exisitingKey = this.keys.findIndex((key) => key.a === a && key.b === b && key.c === c && key.d === d)
    if (exisitingKey >= 0) returnValue = exisitingKey
    else {
      const size = this.keys.push({ a, b, c, d })
      returnValue = size - 1
    }
    return returnValue
  }

  static fillValue ({ key, collection, id, lastState, resolver } : CommitPayload<S> & {resolver: Resolver<S>}) : CommitPayload<S> & {resolver: Resolver<S>} {
    let value
    if (!collection) value = (lastState:any)[key]
    else {
      const record = (lastState:any)[collection].find((record) => record.id === id)
      value = record[key]
    }
    return { key, value, collection, id, lastState, resolver }
  }

  // $FlowFixMe[value-as-type]
  get changes () : Observable<CommitPayload<S>> {
    const commitEffects$ = this.dataCommits$.pipe(
      map(this.findResolver.bind(this)),
      filter(({ resolver }: CommitPayload<S> & {resolver: Resolver<S>}) => resolver),
      groupBy(({ resolver, key, collection, id }: CommitPayload<S> & {resolver: Resolver<S>}) => this.getCompoundKey(resolver.handle, key, collection, id)),
      flatMap((group) => {
        return from(group).pipe(
          map(StateStreamsManager.fillValue),
          skipWhile(({ key, value }) => isNil(value)),
          distinctUntilChanged((prev, { value }) => prev.value === value),
          tap((payload: CommitPayload<S> & {resolver: Resolver<S>}) => this.emit(this.EVENT.LOCK, payload.resolver.affects)),
          switchMap((payload: CommitPayload<S> & {resolver: Resolver<S>}) => from(this.handleResolutions(payload))),
          map((state) => {
            this.logger.debug('Resolver pre-commit payload', state)
            if (state.resolver && state.resolver.affects) this.emit(this.EVENT.UNLOCK, state.resolver.affects)
            return omit(state, 'resolver')
          })
        )
      })
    )

    const directStateChanges$ = this.dataChanges$.pipe(
      map(({ key, value, id, lastState, collection }) => {
        let changes = { [key]: value }
        if (collection) {
          if (this._destructors[key]) {
            const destructor = this._destructors[key] // { displayValue: 'street', value: 'streetId' }
            changes = {
              [destructor.displayValue]: value.displayValue,
              [destructor.value]: value.value
            }
          }
          return getUpdates({ id, ...changes }, lastState[collection], collection)
        }
        return changes
      })
    )
    return merge(directStateChanges$, commitEffects$)
  }

  addResolver (resolver: Resolver<S>) {
    this.resolvers.push(resolver)
  }

  handleChanges (cb: (changes: PartialState) => void) {
    this.subscribtion = this.changes.subscribe((changes) => cb(changes))
  }

  async handleResolutions (payload: CommitPayload<S> & {resolver: Resolver<S>}) : Promise<PartialState & {resolver: Resolver<PartialState>}> {
    let changes = {}
    try {
      if (payload.resolver) changes = await payload.resolver.handle(payload)
    } catch (e) {
      this.logger.error(e)
    }
    return { ...changes, resolver: payload.resolver }
  }

  findResolver (payload: CommitPayload<S>) : CommitPayload<S> & {resolver: Resolver<S>} {
    const resolver = this.resolvers.find(({ triggeredBy }) => triggeredBy.includes(payload.key))
    // $FlowFixMe[incompatible-return]
    return { ...payload, resolver }
  }

  dispose () {
    this.removeAllListeners()
    if (this.subscribtion) this.subscribtion.unsubscribe()
  }
}

export default StateStreamsManager
